/**
 * Las pantallas de la estación calidad, del boceto «Calidad · teléfono» que la
 * gerenta dio por bueno el 7/9 (`../Perejil/public/poc/produccion.html`,
 * `pintarCalidadFono`). La hoja de RE-Q-03 se reparte en pantallas por grupo de
 * determinaciones y la lista de silos sin muestra es la puerta de entrada. Cada
 * silo guarda su hoja: se puede dejar una a medias mientras la humedad tarda
 * sus ocho minutos, volver a la lista y seguir con otra (gerenta, 7/9).
 *
 * No hay orden obligatorio: la hoja muestra una fila por grupo y se carga lo que
 * el laboratorio tiene listo. La regla vigente propone la letra con su porqué; la
 * analista confirma o cambia con motivo. El apto no se pregunta: la Q es apta.
 */

import { actualizarPreferencias } from '../datos/bd';
import { acusarIncidente, bandeja, BorradorYaConfirmado, cargadosEnLaJornada, encolar, mutacionesDeAvisos, mutacionesDeCalidad, type MutacionAEncolar } from '../datos/cola';
import { textoValido } from '../nucleo/produccion';
import { cajaDeTexto, cuantasNotas, listaDeNotas, notaParaLaCola } from './notas';
import type { FilaOutbox } from '../datos/bd';
import { generarUlid } from '../../src/ulid';
import type { LetraCalidad, ReglaCalidad } from '../nucleo/calidad';
import {
	armarPayloadCorreccion,
	armarPayloadMuestra,
	cambiosDeLaHoja,
	cargadasRecientes,
	CAMPOS_MEDIR,
	CAMPOS_TAMICES,
	campoAceptable,
	defectosTexto,
	estadoCorreccion,
	estadoHoja,
	faltantes,
	formatearNumero,
	hojaCorregible,
	hojaDelPayload,
	hojaDesdePayloadRechazado,
	letraAsignada,
	letraDeLaCorreccion,
	motivoRequerido,
	NOMBRE_CAMPO,
	NOMBRE_LETRA,
	letraAlCorregir,
	letraConHumedad,
	porqueDe,
	proponer,
	propuestoEnCero,
	reglaEvaluable,
	sinHumedad,
	sumaTamices,
	textoDe,
	tipearEn,
	UNIDAD_CAMPO,
	VENTANA_DE_CALIDAD_MS,
	type BorradorMuestra,
	type Cargada,
	type CampoNumerico,
	type DefectoDeCatalogo,
	type Grado,
	type HojaParaCorregir,
	type HumedadParaCorregir,
	type MuestraSinHumedad,
	type PantallaHoja,
	type SiloParaMuestra,
	type TarjetaDeHumedad,
} from '../nucleo/muestra';
import { HojaIncompleta } from '../nucleo/muestra';
import { cuandoCorto, fechaDeProduccion, horaCorta, instanteLocal } from '../nucleo/tiempo';
import { botonMantener, colorDeNumero, el, flag, icono, kg, padAnclado, teclado } from './componentes';
import {
	acciones,
	ahoraMs,
	app,
	conPersonaHabitual,
	descartarBorrador,
	guardarBorrador,
	irA,
	leerHoja,
	listarHojas,
	numeroDeSilo,
	personaPremarcada,
	recalcularAvisos,
	recalcularParaMuestra,
	type Vista,
} from './estado';
import { grillaPersonas, nombreDe, pregunta } from './pantallas-captura';
import { queMotivo } from './pantallas-sistema';

const comprometiendo = new Set<string>();

function regla(): ReglaCalidad | null {
	return (app.catalogos?.regla_calidad as ReglaCalidad | null | undefined) ?? null;
}

function catalogoDefectos(): DefectoDeCatalogo[] {
	return (app.catalogos?.defectos ?? []) as DefectoDeCatalogo[];
}

function guardar(b: BorradorMuestra): void {
	b.actualizado_en = Date.now();
	void guardarBorrador(b).catch((error) => console.error('no se pudo guardar la hoja', error));
	app.render();
}

function ir(b: BorradorMuestra, pantalla: PantallaHoja): void {
	b.pantalla = pantalla;
	guardar(b);
}

/** ¿El silo de esta hoja sigue esperando muestra? (Otro teléfono pudo cargarla, o gestión moverlo.) */
function siloVigente(b: BorradorMuestra): boolean {
	return app.paraMuestra.some((s) => s.llenado_id === b.llenado_id);
}

/** Lo que dice la tarjeta de un silo sobre su hoja a medias, si la tiene. */
function estadoCorto(b: BorradorMuestra): string {
	const estado = estadoHoja(b, regla(), catalogoDefectos());
	if (estado.ok) return 'lista para guardar';
	const faltan = faltantes(b);
	return faltan.length > 0 ? `a medias, faltan ${faltan.length}` : `a medias: ${estado.texto.replace(/\.$/, '').toLowerCase()}`;
}

// ── La lista ──────────────────────────────────────────────────────────────

function tarjetaSilo(silo: SiloParaMuestra, hoja: BorradorMuestra | undefined, alTocar: () => void): HTMLElement {
	const caja = el('button', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, numeroDeSilo(silo));
	numero.style.background = colorDeNumero(numeroDeSilo(silo));
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${silo.precinto}`);
	const lote = (app.catalogos?.lotes_campo ?? []).find((l) => l.id === silo.lote_campo_id)?.nombre ?? '—';
	textos.appendChild(el('small', undefined, `${lote} · cerrado ${cuandoCorto(silo.cerrado_en, ahoraMs())}${hoja ? ` · ${estadoCorto(hoja)}` : ''}${cuantasNotas(silo.llenado_id)}`));
	caja.appendChild(textos);
	const peso = el('span', { class: 'peso' });
	peso.appendChild(el('b', undefined, `${kg(silo.neto_g)} kg`));
	peso.appendChild(el('small', undefined, 'netos'));
	caja.appendChild(peso);
	caja.onclick = alTocar;
	return caja;
}

/** La tarjeta de una hoja cuyo silo ya no está en la lista: se abre para verla o descartarla. */
function tarjetaHojaSuelta(hoja: BorradorMuestra): HTMLElement {
	const caja = el('button', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, hoja.silo_numero);
	numero.style.background = colorDeNumero(hoja.silo_numero);
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${hoja.precinto}`);
	textos.appendChild(el('small', undefined, `${hoja.lote} · ${estadoCorto(hoja)}`));
	caja.appendChild(textos);
	caja.onclick = () => irA({ tipo: 'calidad-hoja', borrador: hoja });
	return caja;
}

export function pintarListaCalidad(cuerpo: HTMLElement, pieNodo: HTMLElement, vista: Extract<Vista, { tipo: 'calidad-lista' }>): void {
	const silos = app.paraMuestra;
	const hojas = new Map(vista.hojas.map((h) => [h.llenado_id, h]));
	cuerpo.appendChild(el('div', { class: 'q arriba' }, `Sin muestra · ${silos.length}`));
	cuerpo.appendChild(
		el('div', { class: 'qsub' }, silos.length === 0 ? 'No hay silos esperando muestra.' : 'Los cerrados sin muestra, el más viejo arriba. Tocá uno para abrir su hoja.'),
	);
	if (vista.aviso) cuerpo.appendChild(flag('warn', vista.aviso, ''));
	if (regla() === null) cuerpo.appendChild(flag('warn', 'Todavía no bajó la regla de calidad.', 'Las hojas se pueden cargar; se guardan cuando baje.'));

	const grilla = el('div', { class: 'grid1' });
	for (const silo of silos) grilla.appendChild(tarjetaSilo(silo, hojas.get(silo.llenado_id), () => acciones.abrirHoja(silo)));
	cuerpo.appendChild(grilla);

	// Hojas de silos que dejaron de esperar muestra: otro teléfono la cargó, o gestión
	// lo movió. No se dice por qué —no se sabe—; se conservan hasta que alguien decida.
	// §12.18: una corrección a medias no es una hoja suelta: se retoma desde su hoja en «Cargadas».
	const sueltas = vista.hojas.filter((h) => h.corrige === undefined && !silos.some((s) => s.llenado_id === h.llenado_id));
	if (sueltas.length > 0) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Hojas de silos que ya no esperan muestra · ${sueltas.length}`));
		const grillaSueltas = el('div', { class: 'grid1' });
		for (const hoja of sueltas) grillaSueltas.appendChild(tarjetaHojaSuelta(hoja));
		cuerpo.appendChild(grillaSueltas);
	}

	// La lista la mueven otros teléfonos: se refresca sola cada cinco minutos con señal
	// (baja catálogos si tienen más de diez), y a pedido acá.
	const bajados = app.preferencias.catalogos_bajados_en;
	const actualizar = el('button', { class: 'enlace', type: 'button' }, `Actualizar la lista${bajados ? ` (última vez ${horaCorta(bajados)})` : ''}`);
	actualizar.onclick = async () => {
		// A pedido, la bajada se fuerza: sin esto el sync la saltea hasta los diez minutos
		// y el silo que otro teléfono acaba de muestrear sigue ofrecido (Codex, 11/9).
		await actualizarPreferencias(app.bd, { catalogos_bajados_en: 0 });
		app.preferencias.catalogos_bajados_en = 0;
		app.dispararSync();
	};
	cuerpo.appendChild(actualizar);

	// v6 (§12.9): las que se guardaron sin humedad; se completan cuando el laboratorio la tiene.
	if (app.faltaHumedad.length > 0) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Falta humedad · ${app.faltaHumedad.length}`));
		const grillaHumedad = el('div', { class: 'grid1' });
		for (const m of app.faltaHumedad) grillaHumedad.appendChild(tarjetaSinHumedad(m));
		cuerpo.appendChild(grillaHumedad);
	}

	// §12.18 (antes §12.17, «Humedad cargada · últimas 24 h»): las hojas y las humedades de las últimas 48 horas, de toda la
	// estación; tocar una abre su hoja con los valores (Agata, 15/10: «no puede ver propios datos de lo que cargó»).
	const cargadas = cargadasRecientes(app.hojasParaCorregir, app.humedadesParaCorregir);
	if (cargadas.length > 0) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Cargadas · últimas 48 h · ${cargadas.length}`));
		const grillaCargada = el('div', { class: 'grid1' });
		for (const c of cargadas) grillaCargada.appendChild(tarjetaCargada(c));
		cuerpo.appendChild(grillaCargada);
	}

	void pintarMuestreadasHoy(cuerpo);

	const ver = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Ver lo cargado');
	ver.onclick = () => acciones.verLoCargado();
	pieNodo.appendChild(ver);
}

function tarjetaSinHumedad(m: MuestraSinHumedad): HTMLElement {
	const caja = el('button', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, String(m.silo));
	numero.style.background = colorDeNumero(String(m.silo));
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${m.precinto}`);
	textos.appendChild(el('small', undefined, `muestra ${m.nro_muestra} · ${NOMBRE_LETRA[m.letra_asignada]} · provisoria`));
	caja.appendChild(textos);
	caja.appendChild(el('span', { class: 'ir' }, 'completar ›'));
	caja.onclick = () => irA({ tipo: 'calidad-humedad', muestra: m, humedad: '', personas: personaPremarcada() });
	return caja;
}

/** El número de la muestra; el de una hoja de este teléfono sin acuse todavía no existe. */
const muestraNro = (nro: number | null) => (nro === null ? 'muestra nueva' : `muestra ${nro}`);

/** Cuándo se cargó una humedad, en la hora de la planta: «10:20», «ayer 15:40». */
const cuandoSeCargo = (iso: string) => cuandoCorto(instanteLocal(Date.parse(iso)), ahoraMs(), true);

function tarjetaHumedadCargada(m: HumedadParaCorregir): HTMLElement {
	const caja = el('button', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, String(m.silo));
	numero.style.background = colorDeNumero(String(m.silo));
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${m.precinto} · ${formatearNumero(m.humedad)} %`);
	const detalle = [muestraNro(m.nro_muestra), NOMBRE_LETRA[m.letra_asignada], `${nombreDe(m.persona_id)}, ${cuandoSeCargo(m.humedad_desde)}`];
	if (m.local) detalle.push('esperando señal');
	if (m.con_bolsas === 1) detalle.push('con bolsas');
	textos.appendChild(el('small', undefined, detalle.join(' · ')));
	caja.appendChild(textos);
	caja.appendChild(el('span', { class: 'ir' }, 'corregir ›'));
	caja.onclick = () => irA({ tipo: 'calidad-corregir-humedad', muestra: m, humedad: '', personas: personaPremarcada() });
	return caja;
}

/**
 * Una tarjeta de «Cargadas · últimas 48 h» (§12.18): la hoja con quién la cargó y cuándo, y si alguien la cambió después.
 * Una humedad sin su hoja (un catálogo de antes, o una completada sin señal) va directo a corregirla, como el 11/10.
 */
function tarjetaCargada(c: Cargada): HTMLElement {
	if (c.hoja === null) return tarjetaHumedadCargada(c.humedad as HumedadParaCorregir);
	const h = c.hoja;
	const caja = el('button', { class: 'tile ancho', type: 'button' });
	const numero = el('div', { class: 'cuadro' }, String(h.silo));
	numero.style.background = colorDeNumero(String(h.silo));
	caja.appendChild(numero);
	const textos = el('span', undefined, `precinto ${h.precinto} · ${h.humedad === null ? 'sin humedad' : `${formatearNumero(h.humedad)} %`}`);
	const detalle = [muestraNro(h.nro_muestra), NOMBRE_LETRA[h.letra_asignada], `${nombreDe(h.cargada_por)}, ${cuandoSeCargo(h.hoja_desde)}`];
	if (h.cambiada_en !== null) detalle.push(quienLaCambio(h, false));
	if (h.mutacion_id !== undefined) detalle.push('esperando señal');
	if (h.silo_estado === 'descartado') detalle.push('silo descartado');
	else if (h.con_bolsas === 1) detalle.push('con bolsas');
	textos.appendChild(el('small', undefined, detalle.join(' · ')));
	caja.appendChild(textos);
	caja.appendChild(el('span', { class: 'ir' }, 'ver ›'));
	caja.onclick = () => acciones.verHoja(h);
	return caja;
}

/**
 * Quién cambió la hoja después de cargarla y cuándo: la oficina, envasado con la letra (§12.5; su nombre ya está en la fila
 * de la letra), o una analista. Sin nombre para la tarjeta, con nombre para la hoja.
 */
function quienLaCambio(h: HojaParaCorregir, conNombre: boolean): string {
	const cuando = h.cambiada_en === null ? '' : cuandoSeCargo(h.cambiada_en);
	if (h.desde_gestion === 1 || h.cambiada_por === null) return `corregida en la oficina, ${cuando}`;
	if (h.letra_por !== null && h.cambiada_por === h.letra_por) return `envasado cambió la letra, ${cuando}`;
	return conNombre ? `corregida por ${nombreDe(h.cambiada_por)}, ${cuando}` : `corregida ${cuando}`;
}

/** El borde de las 48 horas: el del catálogo, o el de la hora del último snapshot si el catálogo es de antes del 15/10. */
function desdeDeCalidad(): string | null {
	const delCatalogo = app.catalogos?.hojas_recientes?.desde;
	if (delCatalogo) return delCatalogo;
	const hora = app.preferencias.catalogos_hora_servidor;
	return hora ? new Date(Date.parse(hora) - VENTANA_DE_CALIDAD_MS).toISOString() : null;
}

const siNo = (v: number | null) => (v === null ? '—' : v === 1 ? 'sí' : 'no');

/**
 * §12.18: la hoja de lo cargado, con sus valores: los defectos por nombre y grado, y la letra con su motivo. Abajo, «Corregir
 * la hoja» si llegó hace menos de 48 horas y «Corregir la humedad» si la humedad entró hace menos de 48 (cada botón mira su
 * propio ancla). POC de calidad, iteración 12.
 */
export function pintarVerHoja(cuerpo: HTMLElement, pieNodo: HTMLElement, vista: Extract<Vista, { tipo: 'calidad-ver-hoja' }>): void {
	const h = app.hojasParaCorregir.find((x) => x.llenado_id === vista.hoja.llenado_id) ?? vista.hoja;
	const cat = catalogoDefectos();
	const quien = [`la cargó ${nombreDe(h.cargada_por)}, ${cuandoSeCargo(h.hoja_desde)}`];
	if (h.cambiada_en !== null) quien.push(quienLaCambio(h, true));
	pregunta(cuerpo, `${h.nro_muestra === null ? 'Muestra nueva' : `Muestra nº ${h.nro_muestra}`} · silo ${h.silo}`, `precinto ${h.precinto} · ${h.lote} · ${quien.join(' · ')}`);
	listaDeNotas(cuerpo, h.llenado_id, h.precinto);
	const numero = (v: number | null) => (v === null ? '—' : formatearNumero(v));
	const otro = h.defecto_otro?.trim();
	const letra = `${NOMBRE_LETRA[h.letra_asignada]}${
		h.letra_por !== null
			? ` · la decidió envasado (${nombreDe(h.letra_por)})${h.motivo_decision ? `: «${h.motivo_decision}»` : ''}`
			: h.motivo_decision
				? ` · con motivo: «${h.motivo_decision}»`
				: ' · la que propone la regla'
	}`;
	const filas: Array<[string, string]> = [
		['Densidad · humedad', `${numero(h.densidad)} · ${h.humedad === null ? 'falta' : `${numero(h.humedad)} %`}`],
		['Palos · contaminación grave', `${siNo(h.palos)} · ${siNo(h.contaminacion_grave)}`],
		['Tamices', `${[h.tamiz_sobre_3_5, h.tamiz_sobre_6, h.tamiz_sobre_40, h.tamiz_bajo_40].map(numero).join(' · ')} g`],
		['Materiales extraños', `${defectosTexto(h.defectos, 'material_extranio', cat)}${otro ? ` · otro: ${otro}` : ''}`],
		['Adventicios', defectosTexto(h.defectos, 'adventicio', cat)],
		['Letra', letra],
	];
	const resumen = el('div', { class: 'sum' });
	for (const [etiqueta, texto] of filas) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, texto));
		fila.appendChild(textos);
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);
	if (h.silo_estado === 'descartado') cuerpo.appendChild(flag('warn', 'El silo está descartado.', 'Si fue un error, lo reabre la oficina.'));
	else if (h.con_bolsas === 1) cuerpo.appendChild(flag('warn', 'El precinto ya tiene bolsas.', 'Si una corrección cambia la letra, la oficina revisa sus pallets.'));

	const humedad = app.humedadesParaCorregir.find((m) => m.llenado_id === h.llenado_id && m.muestra_id === h.muestra_id) ?? null;
	if (hojaCorregible(h, desdeDeCalidad())) {
		const corregir = el('button', { class: 'btn go ancho despues', type: 'button' }, 'Corregir la hoja');
		corregir.onclick = () => acciones.corregirHoja(h);
		cuerpo.appendChild(corregir);
	} else {
		cuerpo.appendChild(
			el('div', { class: 'qsub despues' }, `La hoja llegó hace más de 48 horas: la corrige la oficina.${humedad ? ' La humedad entró hace menos y se puede corregir.' : ''}`),
		);
	}
	if (humedad) {
		const corregirHumedad = el('button', { class: 'btn ghost ancho despues', type: 'button' }, 'Corregir la humedad');
		corregirHumedad.onclick = () => irA({ tipo: 'calidad-corregir-humedad', muestra: humedad, humedad: '', personas: personaPremarcada() });
		cuerpo.appendChild(corregirHumedad);
	}
	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver a la lista', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(volver);
}

/** Lo que la tarjeta de «Humedad cargada» necesita, con el estado que deja la mutación (§12.17). */
function tarjetaHumedad(
	m: Omit<TarjetaDeHumedad, 'cargada_en' | 'persona_id' | 'letra_asignada' | 'letra_propuesta'>,
	persona: string,
	letra: LetraCalidad,
	letraPropuesta: LetraCalidad | null,
): TarjetaDeHumedad {
	return { ...m, persona_id: persona, letra_asignada: letra, letra_propuesta: letraPropuesta, cargada_en: new Date(ahoraMs()).toISOString() };
}

/** La tarjeta de una hoja que se guarda con humedad y sin contaminación grave (con contaminación, no se corrige desde acá). */
function tarjetaDeLaHoja(b: BorradorMuestra, payload: Record<string, unknown>): TarjetaDeHumedad | undefined {
	if (typeof payload.humedad !== 'number' || payload.contaminacion_grave === 1) return undefined;
	const grupos = new Map(catalogoDefectos().map((d) => [d.id, d.grupo]));
	const defectos = (payload.defectos as Array<{ defecto_id: string; grado: Grado }>).flatMap((d) => {
		const grupo = grupos.get(d.defecto_id);
		return grupo ? [{ grupo, grado: d.grado }] : [];
	});
	return tarjetaHumedad(
		{
			llenado_id: b.llenado_id,
			precinto: b.precinto,
			silo: Number(b.silo_numero),
			nro_muestra: null,
			tomada_en: payload.tomada_en as string,
			motivo_decision: payload.motivo_decision as string | null,
			densidad: payload.densidad as number,
			tamiz_bajo_40: payload.tamiz_bajo_40 as number,
			palos: payload.palos as number,
			contaminacion_grave: 0,
			defectos: JSON.stringify(defectos),
			con_bolsas: 0,
		},
		payload.persona_id as string,
		payload.letra_asignada as LetraCalidad,
		null,
	);
}

/**
 * v6 (§12.9): «¿Cuánta humedad dio?». Una sola medición sobre una muestra ya guardada: las demás se
 * conservan. Viaja como `muestra.humedad`, una versión nueva; la letra la decide el servidor con la
 * misma regla que se muestra acá.
 */
export function pintarHumedad(cuerpo: HTMLElement, pieNodo: HTMLElement, vista: Extract<Vista, { tipo: 'calidad-humedad' }>): void {
	const m = vista.muestra;
	if (vista.guardada) {
		pregunta(cuerpo, 'Humedad cargada', `Silo ${m.silo} · precinto ${m.precinto} · muestra ${m.nro_muestra}`);
		const valor = Number(vista.humedad.replace(',', '.'));
		const letra = letraConHumedad(m, valor, regla());
		cuerpo.appendChild(flag('ok', `${vista.humedad} %.`, letra === null ? 'La letra la decide el servidor con la regla vigente.' : `La letra queda ${NOMBRE_LETRA[letra]}.`));
		const volver = el('button', { class: 'btn ancho', type: 'button' }, 'Volver a la lista');
		volver.onclick = () => acciones.inicioCalidad();
		pieNodo.appendChild(volver);
		return;
	}
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);
	const { arriba, medio } = padAnclado(contenido, pieNodo);
	pregunta(arriba, '¿Cuánta humedad dio?', `Silo ${m.silo} · precinto ${m.precinto} · muestra ${m.nro_muestra}`);
	if (!conPersonaHabitual(vista.personas)) {
		// Las de calidad: sin la estación, la grilla ofrecía las de verde (Codex, ronda del diff de §12.17, h. 1).
		arriba.appendChild(grillaPersonas(vista.personas, 1, () => app.render(), 'calidad'));
	}
	const lector = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', vista.humedad === '' ? { class: 'apagado' } : undefined, vista.humedad === '' ? '0' : vista.humedad));
	grande.appendChild(el('span', { class: 'unit' }, '%'));
	lector.appendChild(grande);
	arriba.appendChild(lector);

	const valor = vista.humedad === '' ? null : Number(vista.humedad.replace(',', '.'));
	const valida = valor !== null && Number.isFinite(valor) && valor >= 0 && valor <= 100;
	const letra = valida ? letraConHumedad(m, valor, regla()) : null;
	if (vista.humedad !== '' && !valida) {
		medio.appendChild(flag('stop', 'Ese número no puede ser.', 'Va de 0 a 100.'));
	} else if (letra !== null && letra !== m.letra_asignada) {
		medio.appendChild(flag('warn', `Con esta humedad la letra pasa de ${NOMBRE_LETRA[m.letra_asignada]} a ${NOMBRE_LETRA[letra]}.`, 'Si el precinto ya tiene bolsas, la oficina lo ve en «calidad cambió».'));
	} else {
		medio.appendChild(
			flag(
				'ok',
				`La letra ${NOMBRE_LETRA[m.letra_asignada]} quedó provisoria: con la humedad deja de serlo.`,
				m.motivo_decision === null ? '' : 'La decidieron con motivo: se respeta.',
			),
		);
	}
	contenido.appendChild(
		teclado(
			(tecla) => {
				vista.humedad = tipearEn(vista.humedad, tecla);
				app.render();
			},
			() => {
				vista.humedad = vista.humedad.slice(0, -1);
				app.render();
			},
			{ coma: true },
		),
	);
	const cancelar = el('button', { class: 'btn ghost', type: 'button' }, 'Volver');
	cancelar.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(cancelar);
	const guardarBoton = botonMantener('Guardar la humedad', 1500, () => void guardarHumedad(vista, valor as number, letra));
	if (!valida || vista.personas.length === 0) guardarBoton.setAttribute('disabled', '');
	pieNodo.appendChild(guardarBoton);
}

async function guardarHumedad(vista: Extract<Vista, { tipo: 'calidad-humedad' }>, humedad: number, letra: LetraCalidad | null): Promise<void> {
	const m = vista.muestra;
	const persona = vista.personas[0];
	await encolar(
		app.bd,
		[
			{
				id: generarUlid(),
				personas: [persona],
				tipo: 'muestra.humedad',
				payload: { muestra_id: generarUlid(), corrige_a: m.muestra_id, humedad, persona_id: persona },
				entidad: 'muestra',
				registro_id: m.muestra_id,
				resumen: {
					entidad: 'muestra',
					accion: 'humedad',
					fecha: fechaDeProduccion(ahoraMs()),
					silo: String(m.silo),
					precinto: m.precinto,
					letra: letra ?? m.letra_asignada,
					// §12.17: completada, entra a «Humedad cargada» (una con contaminación grave no se corrige desde acá).
					...(m.contaminacion_grave === 1
						? {}
						: {
								tarjeta: tarjetaHumedad(
									{
										llenado_id: m.llenado_id,
										precinto: m.precinto,
										silo: m.silo,
										nro_muestra: m.nro_muestra,
										tomada_en: m.tomada_en,
										motivo_decision: m.motivo_decision,
										densidad: m.densidad,
										tamiz_bajo_40: m.tamiz_bajo_40,
										palos: m.palos,
										contaminacion_grave: m.contaminacion_grave,
										defectos: m.defectos,
										con_bolsas: m.con_bolsas ?? 0,
									},
									persona,
									letra ?? m.letra_asignada,
									letra ?? m.letra_propuesta,
								),
							}),
				},
			},
		],
		[],
		new Date(ahoraMs()).toISOString(),
	);
	recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
	app.dispararSync();
	irA({ ...vista, guardada: true });
}

/**
 * §12.17: «¿Cuánta humedad dio?» para corregir una humedad mal cargada (un número mal tipeado o la de otra muestra;
 * Agata 9/10, «se le mezclan muestras»). La pantalla de completar con sus propios textos (no dice «provisoria»): arriba la de ahora, el visor vacío,
 * la misma no se guarda, y la letra que queda se dice antes de guardar con la regla del catálogo, que viaja en
 * `regla_version`: lo que avisa es lo que escribe el servidor. POC de calidad, iteración 11.
 */
export function pintarCorregirHumedad(cuerpo: HTMLElement, pieNodo: HTMLElement, vista: Extract<Vista, { tipo: 'calidad-corregir-humedad' }>): void {
	const m = vista.muestra;
	if (vista.guardada) {
		// Si el rechazo llega con la pantalla a la vista (otra corrección ganó, cambió la regla, o el padre ya había
		// caído al encolar), se dice acá y no se confirma lo que no entró (Codex, ronda del diff de §12.17, h. 4).
		const rechazo = app.rechazos.get(vista.guardada.mutacion_id);
		if (rechazo !== undefined) {
			pregunta(cuerpo, 'La humedad no se corrigió', `Silo ${m.silo} · precinto ${m.precinto} · ${muestraNro(m.nro_muestra)}`);
			cuerpo.appendChild(flag('stop', 'La oficina no lo aceptó.', `${queMotivo(rechazo)}. Sigue en ${formatearNumero(m.humedad)} %.`));
		} else {
			pregunta(cuerpo, 'Humedad corregida', `Silo ${m.silo} · precinto ${m.precinto} · ${muestraNro(m.nro_muestra)}`);
			const cambio = vista.guardada.letra === m.letra_asignada ? `La letra queda ${NOMBRE_LETRA[vista.guardada.letra]}.` : `La letra pasa de ${NOMBRE_LETRA[m.letra_asignada]} a ${NOMBRE_LETRA[vista.guardada.letra]}.`;
			cuerpo.appendChild(flag('ok', `${formatearNumero(m.humedad)} % → ${formatearNumero(vista.guardada.humedad)} %.`, cambio));
			cuerpo.appendChild(el('div', { class: 'qsub despues' }, app.enLinea ? 'Subiendo.' : 'Sin señal: sube solo cuando haya.'));
		}
		const volver = el('button', { class: 'btn ancho', type: 'button' }, 'Volver a la lista');
		volver.onclick = () => acciones.inicioCalidad();
		pieNodo.appendChild(volver);
		return;
	}
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);
	const { arriba, medio } = padAnclado(contenido, pieNodo);
	pregunta(arriba, '¿Cuánta humedad dio?', `Silo ${m.silo} · precinto ${m.precinto} · ${muestraNro(m.nro_muestra)}`);
	arriba.appendChild(el('div', { class: 'qsub' }, `Ahora: ${formatearNumero(m.humedad)} % · cargó ${nombreDe(m.persona_id)}, ${cuandoSeCargo(m.humedad_desde)}`));
	if (!conPersonaHabitual(vista.personas)) {
		arriba.appendChild(grillaPersonas(vista.personas, 1, () => app.render(), 'calidad'));
	}
	const lector = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', vista.humedad === '' ? { class: 'apagado' } : undefined, vista.humedad === '' ? '0' : vista.humedad));
	grande.appendChild(el('span', { class: 'unit' }, '%'));
	lector.appendChild(grande);
	arriba.appendChild(lector);

	const valor = vista.humedad === '' ? null : Number(vista.humedad.replace(',', '.'));
	const valida = valor !== null && Number.isFinite(valor) && valor >= 0 && valor <= 100;
	const igual = valida && valor === m.humedad;
	const r = regla();
	const letra = valida ? letraAlCorregir(m, valor, r) : null;
	let puede = valida && !igual && letra !== null && !letra.decideLaOficina && r !== null && vista.personas.length > 0;
	if (r === null) {
		medio.appendChild(flag('warn', 'Todavía no bajó la regla de calidad.', 'Sin ella no se sabe qué letra queda: probá de nuevo con señal.'));
		puede = false;
	} else if (vista.humedad !== '' && !valida) {
		medio.appendChild(flag('stop', 'Ese número no puede ser.', 'Va de 0 a 100.'));
	} else if (igual) {
		medio.appendChild(flag('warn', 'Es la misma humedad que ya tiene.', 'No hay nada que corregir.'));
	} else if (letra?.decideLaOficina) {
		medio.appendChild(flag('stop', 'Con la regla de hoy, la letra de esta muestra la decide la oficina.', 'Pediles que corrijan la humedad desde Administrar.'));
	} else if (letra !== null && m.motivo_decision !== null) {
		medio.appendChild(flag('ok', `La letra ${NOMBRE_LETRA[m.letra_asignada]} queda:`, `la decidieron con motivo («${m.motivo_decision}»).`));
	} else if (letra !== null && letra.letra !== m.letra_asignada) {
		medio.appendChild(
			flag('warn', `Con esta humedad la letra pasa de ${NOMBRE_LETRA[m.letra_asignada]} a ${NOMBRE_LETRA[letra.letra]}.`, m.con_bolsas === 1 ? 'El precinto ya tiene bolsas: la oficina revisa sus pallets.' : ''),
		);
	} else if (letra !== null) {
		medio.appendChild(flag('ok', `La letra queda ${NOMBRE_LETRA[letra.letra]}.`, ''));
	}
	contenido.appendChild(
		teclado(
			(tecla) => {
				vista.humedad = tipearEn(vista.humedad, tecla);
				app.render();
			},
			() => {
				vista.humedad = vista.humedad.slice(0, -1);
				app.render();
			},
			{ coma: true },
		),
	);
	const cancelar = el('button', { class: 'btn ghost', type: 'button' }, 'Volver');
	cancelar.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(cancelar);
	const guardarBoton = botonMantener('Guardar la corrección', 1500, () => {
		if (letra !== null && r !== null) void guardarCorreccion(vista, valor as number, letra.letra, r);
	});
	if (!puede) guardarBoton.setAttribute('disabled', '');
	pieNodo.appendChild(guardarBoton);
}

async function guardarCorreccion(vista: Extract<Vista, { tipo: 'calidad-corregir-humedad' }>, humedad: number, letra: LetraCalidad, r: ReglaCalidad): Promise<void> {
	const m = vista.muestra;
	const persona = vista.personas[0];
	const { muestra_id: _base, humedad: _antes, humedad_desde: _desde, local: _local, mutacion_id: padre, ...datos } = m;
	const id = generarUlid();
	await encolar(
		app.bd,
		[
			{
				id,
				personas: [persona],
				tipo: 'muestra.corregir_humedad',
				payload: { muestra_id: generarUlid(), corrige_a: m.muestra_id, humedad, regla_version: r.version, persona_id: persona },
				entidad: 'muestra',
				registro_id: m.muestra_id,
				// Si la versión que se corrige sigue en la cola, la corrección cuelga de ella: el padre se fijó al elegir la
				// tarjeta, no se busca ahora (si cayó mientras la pantalla estaba abierta, `encolar` lo sabe).
				...(padre === undefined ? {} : { padre_id: padre }),
				resumen: {
					entidad: 'muestra',
					accion: 'humedad_corregida',
					fecha: fechaDeProduccion(ahoraMs()),
					silo: String(m.silo),
					precinto: m.precinto,
					letra,
					humedad,
					humedad_antes: m.humedad,
					tarjeta: tarjetaHumedad(datos, persona, letra, m.motivo_decision === null ? letra : m.letra_propuesta),
				},
			},
		],
		[],
		new Date(ahoraMs()).toISOString(),
	);
	// Un hijo cuyo padre ya había caído nace en la bandeja con `padre_rechazado`: el comprobante lo dice ya.
	const caida = (await bandeja(app.bd)).find((f) => f.id === id);
	if (caida) app.rechazos.set(id, caida.motivo ?? '');
	recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
	app.dispararSync();
	irA({ ...vista, guardada: { humedad, letra, mutacion_id: id } });
}

/** Lo que salió de ESTE teléfono en la jornada: el laboratorio entero no se ve desde acá. */
async function pintarMuestreadasHoy(cuerpo: HTMLElement): Promise<void> {
	const { fecha, recibos, pendientes } = await cargadosEnLaJornada(app.bd, ahoraMs());
	const filas = [
		// Solo las hojas: completar o corregir una humedad no es una muestra más (contaba cada humedad, 11/10). Pero
		// una hoja de hoy de este teléfono cuyo recibo pisó después su humedad o su corrección sigue contando: la
		// cadena del recibo guarda la jornada de la hoja (Codex, ronda del diff de §12.17, h. 3).
		...recibos
			.filter((r) => r.resumen.entidad === 'muestra' && (r.resumen.accion === undefined || r.cadena?.hoja === fecha))
			.map((r) => ({ resumen: r.resumen, nro: r.nro_muestra ?? null, subido: true })),
		...pendientes.filter((f) => f.resumen.entidad === 'muestra' && f.resumen.accion === undefined).map((f) => ({ resumen: f.resumen, nro: null, subido: false })),
	];
	if (filas.length === 0) return;
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, `Muestreadas desde este teléfono · hoy · ${filas.length}`));
	const grilla = el('div', { class: 'grid1' });
	for (const f of filas) {
		if (f.resumen.entidad !== 'muestra') continue;
		const caja = el('div', { class: 'tile ancho hecho' });
		const numero = el('div', { class: 'cuadro' }, f.resumen.silo);
		numero.style.background = colorDeNumero(f.resumen.silo);
		caja.appendChild(numero);
		const textos = el('span', undefined, `precinto ${f.resumen.precinto}`);
		textos.appendChild(el('small', undefined, `${NOMBRE_LETRA[f.resumen.letra]} · ${f.subido ? (f.nro === null ? 'subida' : `muestra ${f.nro}`) : 'esperando señal'}`));
		caja.appendChild(textos);
		grilla.appendChild(caja);
	}
	cuerpo.appendChild(grilla);
}

// ── La hoja ───────────────────────────────────────────────────────────────

export function pintarHoja(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	switch (b.pantalla) {
		case 'quien':
			return pQuien(cuerpo, pieNodo, b);
		case 'medir':
			return pCampos(cuerpo, pieNodo, b, 'Densidad y humedad', 'Tocá el campo y tipeá. Si la humedad todavía no está, se puede guardar sin ella: la letra queda provisoria y la completás después desde la lista.', CAMPOS_MEDIR);
		case 'palos':
			return pPalos(cuerpo, pieNodo, b);
		case 'tamices':
			return pCampos(cuerpo, pieNodo, b, 'Tamices', 'En gramos, sobre 100 g de muestra.', CAMPOS_TAMICES);
		case 'extranios':
			return pDefectos(cuerpo, pieNodo, b, 'material_extranio');
		case 'adventicios':
			return pDefectos(cuerpo, pieNodo, b, 'adventicio');
		case 'resultado':
			return pResultado(cuerpo, pieNodo, b);
		case 'nota':
			return pNota(cuerpo, pieNodo, b);
		default:
			return pResumenHoja(cuerpo, pieNodo, b);
	}
}

/** Silo, precinto y lote arriba de cada subpantalla: con dos hojas a medias, hay que saber cuál es. */
function referencia(cuerpo: HTMLElement, b: BorradorMuestra): void {
	const caja = el('div', { class: 'ref' });
	caja.appendChild(el('span', undefined, 'muestra de'));
	caja.appendChild(el('b', undefined, `silo ${b.silo_numero}`));
	caja.appendChild(el('span', undefined, `precinto ${b.precinto} · ${b.lote}`));
	cuerpo.appendChild(caja);
}

function pieHoja(pieNodo: HTMLElement, b: BorradorMuestra, derecho?: { texto: string; deshabilitado?: boolean; alTocar: () => void }): void {
	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => ir(b, 'hoja');
	pieNodo.appendChild(volver);
	if (derecho) {
		const boton = el('button', { class: 'btn go', type: 'button' }, derecho.texto);
		if (derecho.deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = derecho.alTocar;
		pieNodo.appendChild(boton);
	}
}

function textoLetra(letra: LetraCalidad | null): string {
	return letra === null ? 'sin letra' : NOMBRE_LETRA[letra];
}

type FilaDeLaHoja = [string, string, PantallaHoja | null];

/** Una fila por grupo con lo cargado hasta ahora; tocar «cargar» abre su pantalla. */
function filasDeLaHoja(b: BorradorMuestra, r: ReglaCalidad | null, cat: DefectoDeCatalogo[]): FilaDeLaHoja[] {
	const propuesta = proponer(b, r, cat);
	const letra = letraAsignada(b, propuesta);
	const valor = (campo: CampoNumerico) => (textoDe(b, campo) === '' ? '—' : textoDe(b, campo));
	const siNo = (v: 0 | 1 | null) => (v === null ? '—' : v === 1 ? 'sí' : 'no');
	const marcados = (grupo: DefectoDeCatalogo['grupo']) => cat.filter((d) => d.grupo === grupo && b.defectos[d.id] !== undefined).length;
	const cuantos = (n: number) => (n === 0 ? 'ninguno' : `${n} marcado${n === 1 ? '' : 's'}`);
	const tamices = sumaTamices(b, r);
	const resultado =
		propuesta === null
			? 'faltan mediciones'
			: letra === null
				? 'la regla no decide: elegí P o L'
				: `${textoLetra(letra)} · ${motivoRequerido(b, propuesta) ? 'cambiada' : 'propuesta'}`;
	// En un teléfono personal, la analista la fija el enrolamiento (misma regla que
	// producción, 10/9): la fila se ve y no se cambia. En el muleto, es un toque.
	return [
		[b.corrige ? 'Quién corrige' : 'Quién analiza', b.personas[0] ? nombreDe(b.personas[0]) : '—', conPersonaHabitual(b.personas) ? null : 'quien'],
		['Densidad · humedad', `${valor('densidad')} · ${sinHumedad(b) ? 'falta (la letra queda provisoria)' : `${valor('humedad')} %`}`, 'medir'],
		['Palos · contaminación grave', `${siNo(b.palos)} · ${siNo(b.contaminacion_grave)}`, 'palos'],
		['Tamices', `${CAMPOS_TAMICES.map(valor).join(' · ')} g${tamices.completa ? ` · suman ${formatearNumero(tamices.suma)}` : ''}`, 'tamices'],
		['Materiales extraños', `${cuantos(marcados('material_extranio'))}${b.defecto_otro?.trim() ? ` · otro: ${b.defecto_otro.trim()}` : ''}`, 'extranios'],
		['Adventicios', cuantos(marcados('adventicio')), 'adventicios'],
		['Resultado', resultado, 'resultado'],
		['Nota para el silo', textoValido(b.nota_texto) ?? 'ninguna (opcional)', 'nota'],
	];
}

function pintarFilas(cuerpo: HTMLElement, b: BorradorMuestra, filas: FilaDeLaHoja[]): void {
	const resumen = el('div', { class: 'sum' });
	for (const [etiqueta, texto, pantalla] of filas) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, texto));
		fila.appendChild(textos);
		if (pantalla !== null) {
			const cargar = el('button', { class: 'fix', type: 'button' }, 'cargar');
			cargar.onclick = () => ir(b, pantalla);
			fila.appendChild(cargar);
		}
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);
}

/**
 * §12.18: «Corregir la hoja», la misma hoja prellenada. Arriba, qué cambió y qué letra queda; la letra de envasado queda
 * fija (sin la fila del resultado) y la nota del silo no va (se agrega desde la lista). POC de calidad, iteración 12.
 */
function pResumenCorreccion(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra, base: HojaParaCorregir): void {
	const r = regla();
	const cat = catalogoDefectos();
	const letra = letraDeLaCorreccion(b, r, cat);
	const cabecera = el('div', { class: 'qrow' });
	cabecera.appendChild(el('div', { class: 'q' }, `Corregir la hoja · ${base.nro_muestra === null ? 'muestra nueva' : `muestra nº ${base.nro_muestra}`}`));
	cabecera.appendChild(el('div', { class: `letra-cab${letra === null ? '' : letra === 'descarte' ? ' desc' : ' con'}` }, textoLetra(letra)));
	cuerpo.appendChild(cabecera);
	cuerpo.appendChild(el('div', { class: 'qsub' }, `Silo ${base.silo} · precinto ${base.precinto} · la cargó ${nombreDe(base.cargada_por)}, ${cuandoSeCargo(base.hoja_desde)}`));
	const cambios = cambiosDeLaHoja(base, b, cat);
	if (cambios.length > 0) cuerpo.appendChild(flag('ok', 'Cambia:', `${cambios.join('; ')}.`));
	if (letra !== null && letra !== base.letra_asignada) {
		cuerpo.appendChild(
			flag('warn', `La letra pasa de ${NOMBRE_LETRA[base.letra_asignada]} a ${NOMBRE_LETRA[letra]}.`, base.con_bolsas === 1 ? 'El precinto ya tiene bolsas: la oficina revisa sus pallets.' : ''),
		);
	}
	if (base.letra_por !== null) cuerpo.appendChild(flag('ok', `La letra ${NOMBRE_LETRA[base.letra_asignada]} la decidió envasado (${nombreDe(base.letra_por)}):`, 'se respeta.'));
	pintarFilas(
		cuerpo,
		b,
		filasDeLaHoja(b, r, cat).filter(([, , pantalla]) => pantalla !== 'nota' && !(base.letra_por !== null && pantalla === 'resultado')),
	);
	const estado = estadoCorreccion(b, r, cat);
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, estado.texto));
	const tirar = el('button', { class: 'enlace', type: 'button' }, 'Descartar la corrección');
	tirar.onclick = async () => {
		await descartarBorrador(b).catch(() => null);
		irA({ tipo: 'calidad-ver-hoja', hoja: base });
	};
	cuerpo.appendChild(tirar);

	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver a la hoja', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => irA({ tipo: 'calidad-ver-hoja', hoja: base });
	pieNodo.appendChild(volver);
	const guardarBoton = botonMantener('Guardar la corrección', 1500, () => void confirmarCorreccion(b));
	if (!estado.ok) guardarBoton.setAttribute('disabled', '');
	pieNodo.appendChild(guardarBoton);
}

function pResumenHoja(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	if (b.corrige !== undefined) return pResumenCorreccion(cuerpo, pieNodo, b, b.corrige);
	const r = regla();
	const cat = catalogoDefectos();
	const propuesta = proponer(b, r, cat);
	const letra = letraAsignada(b, propuesta);
	const vigente = siloVigente(b);

	const cabecera = el('div', { class: 'qrow' });
	cabecera.appendChild(el('div', { class: 'q' }, `Precinto ${b.precinto} · silo ${b.silo_numero}`));
	cabecera.appendChild(el('div', { class: `letra-cab${letra === null ? '' : letra === 'descarte' ? ' desc' : ' con'}` }, textoLetra(letra)));
	cuerpo.appendChild(cabecera);
	cuerpo.appendChild(el('div', { class: 'qsub' }, `${b.lote} · ${kg(b.neto_g)} kg netos · cerrado ${cuandoCorto(b.cerrado_en, ahoraMs())}`));

	if (!vigente) {
		cuerpo.appendChild(flag('warn', 'Este silo ya no espera muestra.', 'Otro teléfono cargó la suya, o la oficina lo movió. Esta hoja se puede descartar.'));
	}
	// v6 (§12.4): lo que producción (u otra estación) anotó del silo.
	listaDeNotas(cuerpo, b.llenado_id, b.precinto);

	pintarFilas(cuerpo, b, filasDeLaHoja(b, r, cat));

	const estado = estadoHoja(b, r, cat);
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, estado.texto));

	if (!vigente) {
		const tirar = el('button', { class: 'enlace', type: 'button' }, 'Descartar esta hoja');
		tirar.onclick = async () => {
			await descartarBorrador(b).catch(() => null);
			acciones.inicioCalidad();
		};
		cuerpo.appendChild(tirar);
	}

	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver a la lista', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(volver);
	const guardarBoton = botonMantener('Guardar la muestra', 1500, () => void confirmar(b));
	if (!estado.ok || !vigente) guardarBoton.setAttribute('disabled', '');
	pieNodo.appendChild(guardarBoton);
}

/** v6 (§12.4): una nota para el silo, que ven producción y envasado. */
function pNota(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	pregunta(cuerpo, 'Nota para el silo', 'Opcional. La ven producción y envasado cuando miran este precinto.');
	referencia(cuerpo, b);
	listaDeNotas(cuerpo, b.llenado_id, b.precinto);
	cajaDeTexto(cuerpo, () => b.nota_texto, (v) => (b.nota_texto = v), 'Ej.: olor a humedad, mirar al embolsar', () => void guardar(b));
	pieHoja(pieNodo, b, { texto: 'Listo', alTocar: () => ir(b, 'hoja') });
}

function pQuien(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	pregunta(cuerpo, '¿Quién analiza?', 'Tocá tu nombre. Se recuerda para la próxima hoja.');
	referencia(cuerpo, b);
	cuerpo.appendChild(grillaPersonas(b.personas, 1, () => guardar(b), 'calidad'));
	pieHoja(pieNodo, b, { texto: 'Listo', deshabilitado: b.personas.length === 0, alTocar: () => ir(b, 'hoja') });
}

/** Densidad y humedad, o los cuatro tamices: los campos arriba, el teclado con coma abajo. */
function pCampos(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra, titulo: string, sub: string, campos: CampoNumerico[]): void {
	// Pad anclado como en el resto de las estaciones: los campos arriba, los avisos en
	// la franja del medio, el teclado fijo sobre el pie. La hoja recibe el `.cuerpo`
	// directo, así que el `.appbody` que el pad espera se arma acá.
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);
	const { arriba, medio } = padAnclado(contenido, pieNodo);
	pregunta(arriba, titulo, sub);
	referencia(arriba, b);
	if (b.campo === null || !campos.includes(b.campo)) b.campo = campos[0];
	const activo = b.campo;

	const grilla = el('div', { class: campos.length > 2 ? 'medidas cuatro' : 'medidas' });
	for (const campo of campos) {
		const caja = el('div', { class: 'medida' });
		caja.appendChild(el('label', undefined, NOMBRE_CAMPO[campo]));
		const texto = textoDe(b, campo);
		const mal = texto !== '' && !campoAceptable(b, campo);
		const val = el('button', { class: `val${campo === activo ? ' on' : ''}${mal ? ' mal' : ''}${propuestoEnCero(b, campo) ? ' propuesto' : ''}`, type: 'button' }, texto === '' ? '—' : texto);
		if (UNIDAD_CAMPO[campo]) val.appendChild(el('small', undefined, UNIDAD_CAMPO[campo]));
		val.onclick = () => {
			b.campo = campo;
			guardar(b);
		};
		caja.appendChild(val);
		grilla.appendChild(caja);
	}
	arriba.appendChild(grilla);

	if (campos === CAMPOS_TAMICES) {
		const r = regla();
		const suma = sumaTamices(b, r);
		if (suma.completa) {
			medio.appendChild(
				flag(
					suma.fuera ? 'warn' : 'ok',
					`Suman ${formatearNumero(suma.suma)} g.`,
					suma.fuera ? `Tienen que dar 100 ± ${r?.tolerancia_suma_tamices ?? '—'}. Se guarda igual.` : 'Dentro del margen.',
				),
			);
		}
	}
	if (textoDe(b, activo) !== '' && !campoAceptable(b, activo)) {
		medio.appendChild(flag('stop', 'Ese número no puede ser.', activo === 'densidad' ? 'La densidad tiene que ser mayor que cero.' : 'Va de 0 a 100.'));
	}
	if (propuestoEnCero(b, activo)) medio.appendChild(el('div', { class: 'qsub despues' }, 'Propuesto en cero: el primer dígito lo reemplaza.'));

	contenido.appendChild(
		teclado(
			(tecla) => {
				const actual = propuestoEnCero(b, activo) ? '' : (b[activo] ?? '');
				b[activo] = tipearEn(actual, tecla);
				guardar(b);
			},
			() => {
				b[activo] = (b[activo] ?? '').slice(0, -1);
				guardar(b);
			},
			{ coma: true },
		),
	);

	const indice = campos.indexOf(activo);
	const siguiente = campos[indice + 1];
	pieHoja(pieNodo, b, {
		texto: siguiente ? `Siguiente: ${NOMBRE_CAMPO[siguiente]}` : 'Listo',
		alTocar: () => {
			// El «sobre 3,5» propuesto en cero queda en cero al pasar de largo.
			if (activo === 'tamiz_sobre_3_5' && b.tamiz_sobre_3_5 === null) b.tamiz_sobre_3_5 = '0';
			if (siguiente) {
				b.campo = siguiente;
				guardar(b);
			} else {
				ir(b, 'hoja');
			}
		},
	});
}

function pPalos(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	pregunta(cuerpo, 'Palos y contaminación', 'Palos de más de 1,5 cm: sí o no. La contaminación grave descarta el silo.');
	referencia(cuerpo, b);
	const sino = (etiqueta: string, leer: () => 0 | 1 | null, escribir: (v: 0 | 1) => void, grave: boolean) => {
		const caja = el('div', { class: 'sino' });
		caja.appendChild(el('label', undefined, etiqueta));
		const chips = el('div', { class: 'chips' });
		for (const [texto, valor] of [
			['no', 0],
			['sí', 1],
		] as const) {
			const chip = el('button', { class: `chip${leer() === valor ? (grave && valor === 1 ? ' stopsel' : ' sel') : ''}`, type: 'button' }, texto);
			chip.onclick = () => {
				escribir(valor);
				guardar(b);
			};
			chips.appendChild(chip);
		}
		caja.appendChild(chips);
		return caja;
	};
	cuerpo.appendChild(
		sino(
			'Palos > 1,5 cm',
			() => b.palos,
			(v) => {
				b.palos = v;
			},
			false,
		),
	);
	cuerpo.appendChild(
		sino(
			'Contaminación grave',
			() => b.contaminacion_grave,
			(v) => {
				b.contaminacion_grave = v;
			},
			true,
		),
	);
	const conBolsas = b.corrige !== undefined && b.corrige.contaminacion_grave === 0 && b.corrige.con_bolsas === 1;
	if (b.contaminacion_grave === 1 && conBolsas) {
		// §12.18: descartaría un silo con material embolsado, que r. 19 le sigue negando al teléfono (`contaminacion_con_bolsas`).
		cuerpo.appendChild(flag('stop', 'El precinto ya tiene bolsas.', 'Una contaminación grave descartaría un silo ya embolsado: eso lo carga la oficina.'));
	} else if (b.contaminacion_grave === 1) {
		cuerpo.appendChild(flag('stop', 'Con contaminación grave el silo entero se descarta.', 'Queda descartado cuando la muestra suba.'));
	}
	pieHoja(pieNodo, b, { texto: 'Listo', deshabilitado: b.palos === null || b.contaminacion_grave === null, alTocar: () => ir(b, 'hoja') });
}

const GRADOS: Array<[Grado | 'N', string]> = [
	['N', '—'],
	['P', 'P'],
	['B', 'B'],
	['M', 'M'],
];

/** Un defecto por fila, cuatro segmentos (— · P · B · M): un toque, un estado (r. 66). */
function pDefectos(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra, grupo: DefectoDeCatalogo['grupo']): void {
	pregunta(cuerpo, grupo === 'material_extranio' ? 'Materiales extraños' : 'Adventicios', '— · poco · bastante · mucho');
	referencia(cuerpo, b);
	// Los activos del catálogo, más los que esta hoja ya marcó aunque gestión los haya apagado.
	const defectos = catalogoDefectos()
		.filter((d) => d.grupo === grupo && (d.activo === 1 || b.defectos[d.id] !== undefined))
		.sort((x, y) => x.orden - y.orden);
	const lista = el('div', { class: 'defs' });
	// La leyenda de los cuatro segmentos, pegada arriba: catorce filas scrollean y el
	// subtítulo se va con ellas (revisión del 12/9).
	const leyenda = el('div', { class: 'def leyenda' });
	leyenda.appendChild(el('span', { class: 'nombre' }, ''));
	const rotulos = el('div', { class: 'segmentos' });
	for (const texto of ['—', 'poco', 'bastante', 'mucho']) rotulos.appendChild(el('span', { class: 'rotulo' }, texto));
	leyenda.appendChild(rotulos);
	lista.appendChild(leyenda);
	for (const defecto of defectos) {
		const fila = el('div', { class: 'def' });
		fila.appendChild(el('span', { class: 'nombre' }, defecto.nombre));
		const segmentos = el('div', { class: 'segmentos' });
		const actual = b.defectos[defecto.id] ?? 'N';
		for (const [grado, texto] of GRADOS) {
			const boton = el('button', { class: `segmento${actual === grado ? ' sel' : ''}`, type: 'button', 'data-g': grado }, texto);
			boton.onclick = () => {
				if (grado === 'N') delete b.defectos[defecto.id];
				else b.defectos[defecto.id] = grado;
				guardar(b);
			};
			segmentos.appendChild(boton);
		}
		fila.appendChild(segmentos);
		lista.appendChild(fila);
	}
	cuerpo.appendChild(lista);
	// Los marcados que el catálogo ya no conoce: se ven, y hay que destildarlos para guardar.
	const desconocidos = Object.keys(b.defectos).filter((id) => !catalogoDefectos().some((d) => d.id === id));
	if (desconocidos.length > 0 && grupo === 'material_extranio') {
		const aviso = flag('warn', 'Hay un defecto marcado que ya no está en el catálogo.', 'Destildalo para poder guardar.');
		const quitar = el('button', { class: 'enlace', type: 'button' }, 'Destildar los que ya no existen');
		quitar.onclick = () => {
			for (const id of desconocidos) delete b.defectos[id];
			guardar(b);
		};
		cuerpo.appendChild(aviso);
		cuerpo.appendChild(quitar);
	}
	if (grupo === 'material_extranio') {
		// «Otros, cuál»: texto libre aparte de las filas del catálogo; no entra en el
		// árbol, gestión lo normaliza después (§4.1 `defecto_otro`).
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Otro material extraño, ¿cuál? Opcional.'));
		const otro = el('input', { class: 'campo', autocomplete: 'off', maxlength: '100', placeholder: 'Ej.: vidrio' });
		otro.value = b.defecto_otro ?? '';
		otro.oninput = () => {
			b.defecto_otro = otro.value;
			b.actualizado_en = Date.now();
			void guardarBorrador(b);
		};
		cuerpo.appendChild(otro);
	}
	pieHoja(pieNodo, b, { texto: 'Listo', alTocar: () => ir(b, 'hoja') });
}

function pResultado(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra): void {
	const r = regla();
	const cat = catalogoDefectos();
	pregunta(cuerpo, 'Resultado', 'La regla propone; vos confirmás o cambiás con motivo.');
	referencia(cuerpo, b);
	const propuesta = proponer(b, r, cat);
	const asignada = letraAsignada(b, propuesta);
	// La versión con la que la analista decidió se fija con SU acción —tocar una letra,
	// dar por visto el resultado—, nunca al pintar: un sync que baje otra regla mientras
	// mira la pantalla no puede dar la revisión por hecha (Codex, ronda del diff, 11/9).
	const darPorVisto = () => {
		b.regla_version = r?.version ?? null;
	};
	if (b.regla_version !== null && r !== null && b.regla_version !== r.version) {
		cuerpo.appendChild(flag('warn', 'La regla de calidad cambió desde que viste el resultado.', 'Mirá lo que propone ahora y volvé a dar Listo.'));
	}

	const caja = el('div', { class: `propuesta${propuesta?.letra === null ? ' sin' : ''}${propuesta?.letra === 'descarte' ? ' desc' : ''}` });
	caja.appendChild(el('div', { class: 'lb' }, r === null ? 'sin regla de calidad' : `la regla propone · v${r.version}`));
	caja.appendChild(el('div', { class: 'letra' }, propuesta === null ? '…' : propuesta.letra === null ? '—' : NOMBRE_LETRA[propuesta.letra]));
	caja.appendChild(
		el(
			'div',
			{ class: 'por' },
			r === null
				? 'todavía no bajó de la oficina'
				: !reglaEvaluable(r)
					? 'esta regla es más nueva que la app: hay que actualizarla'
					: propuesta === null
						? 'faltan densidad, humedad, polvo, palos o contaminación'
						: porqueDe(propuesta, r, b),
		),
	);
	cuerpo.appendChild(caja);
	if (sinHumedad(b)) {
		cuerpo.appendChild(flag('warn', 'Falta la humedad: se guarda igual, con la letra provisoria.', 'El silo pasa a «Falta humedad» y la completás cuando la tengas.'));
	}

	const letras = el('div', { class: 'letras' });
	for (const letra of ['Q', 'P', 'L', 'descarte'] as LetraCalidad[]) {
		const boton = el('button', { class: `letra-b${asignada === letra ? ` sel${letra === 'descarte' ? ' desc' : ''}` : ''}`, type: 'button' }, letra === 'descarte' ? 'Descarte' : letra);
		boton.onclick = () => {
			b.letra_manual = letra;
			darPorVisto();
			guardar(b);
		};
		letras.appendChild(boton);
	}
	cuerpo.appendChild(letras);
	// Tocar una letra la fija, aunque sea la misma que la propuesta (como en el boceto);
	// acá se puede volver a que la regla decida, cosa que el boceto no ofrecía.
	if (b.letra_manual !== null && propuesta?.letra !== null) {
		const seguir = el('button', { class: 'enlace', type: 'button' }, 'Dejar la que propone la regla');
		seguir.onclick = () => {
			b.letra_manual = null;
			b.motivo = '';
			guardar(b);
		};
		cuerpo.appendChild(seguir);
	}

	if (propuesta !== null && motivoRequerido(b, propuesta)) {
		const texto = el('textarea', {
			class: 'nota',
			rows: '3',
			maxlength: '500',
			placeholder: propuesta.letra === null ? 'Por qué P o L: hay adventicios y la regla no decide…' : 'Por qué cambiás lo que propone la regla…',
		});
		texto.value = b.motivo;
		texto.oninput = () => {
			b.motivo = texto.value;
			b.actualizado_en = Date.now();
			void guardarBorrador(b);
		};
		cuerpo.appendChild(texto);
	}
	pieHoja(pieNodo, b, {
		texto: 'Listo',
		alTocar: () => {
			darPorVisto();
			ir(b, 'hoja');
		},
	});
}

// ── Guardar ───────────────────────────────────────────────────────────────

async function confirmar(b: BorradorMuestra): Promise<void> {
	if (b.corrige !== undefined) return confirmarCorreccion(b);
	if (comprometiendo.has(b.capture_id)) return;
	comprometiendo.add(b.capture_id);
	try {
		const r = regla();
		if (r === null) {
			irA({ tipo: 'calidad-lista', hojas: [], aviso: 'Todavía no bajó la regla de calidad: la hoja queda guardada.' });
			return;
		}
		const payload = armarPayloadMuestra(b, r, catalogoDefectos());
		const guardadaEn = new Date(ahoraMs()).toISOString();
		const persona = payload.persona_id as string;
		// §12.18: la hoja entera en el resumen, para «Cargadas · últimas 48 h» sin señal.
		const hoja = hojaDelPayload(payload, catalogoDefectos(), {
			raiz_id: b.muestra_id,
			llenado_id: b.llenado_id,
			nro_muestra: null,
			silo: Number(b.silo_numero),
			precinto: b.precinto,
			lote: b.lote,
			silo_estado: payload.contaminacion_grave === 1 ? 'descartado' : 'cerrado',
			letra_propuesta: proponer(b, r, catalogoDefectos())?.letra ?? null,
			letra_por: null,
			cargada_por: persona,
			hoja_desde: guardadaEn,
			cambiada_por: null,
			cambiada_en: null,
			desde_gestion: 0,
			humedad_desde: payload.humedad === null ? null : guardadaEn,
			humedad_por: payload.humedad === null ? null : persona,
			con_bolsas: 0,
		});
		const mutacion: MutacionAEncolar = {
			id: b.mutacion_id,
			tipo: 'muestra.crear',
			payload,
			entidad: 'muestra',
			registro_id: b.muestra_id,
			personas: [...b.personas],
			// La jornada de la toma, no la de ahora: una hoja de las 05:58 terminada a
			// las 06:06 sigue siendo de ayer.
			resumen: {
				entidad: 'muestra',
				fecha: fechaDeProduccion(b.tomada_ms),
				silo: b.silo_numero,
				precinto: b.precinto,
				letra: payload.letra_asignada as LetraCalidad,
				// §12.17: con humedad, la hoja entra a «Humedad cargada» aunque no haya señal.
				...(tarjetaDeLaHoja(b, payload) === undefined ? {} : { tarjeta: tarjetaDeLaHoja(b, payload) }),
				hoja,
			},
		};
		// v6 (§12.4): la nota, si hay, viaja en la misma confirmación (ids de una hoja vieja, una vez).
		const nota = textoValido(b.nota_texto);
		if (nota !== null && (!b.nota_id || !b.nota_mutacion_id)) {
			b.nota_id = generarUlid();
			b.nota_mutacion_id = generarUlid();
			await guardar(b);
		}
		const conNota = nota !== null && b.nota_id && b.nota_mutacion_id ? [notaParaLaCola(b.nota_mutacion_id, b.nota_id, b.llenado_id, b.precinto, nota, b.personas)] : [];
		await encolar(app.bd, [mutacion, ...conNota], [b.capture_id], new Date(ahoraMs()).toISOString());
		recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
		// La lista se rehace ANTES de volver: el silo recién muestreado no puede seguir ofrecido.
		recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
		// En el muleto, la analista elegida se recuerda para la próxima hoja.
		if (personaPremarcada().length === 0 && b.personas[0]) {
			await actualizarPreferencias(app.bd, { ultima_analista_id: b.personas[0] });
			app.preferencias.ultima_analista_id = b.personas[0];
		}
		app.dispararSync();
		irA({ tipo: 'calidad-listo', borrador: b, letra: payload.letra_asignada as LetraCalidad });
	} catch (error) {
		// La hoja no está lista (la regla cambió entre ver el resultado y confirmar): se queda en la
		// pantalla, que ahora dice qué revisar (Codex, ronda del diff, 14/9).
		if (error instanceof HojaIncompleta) {
			app.render();
			return;
		}
		if (error instanceof BorradorYaConfirmado) {
			// Una copia vieja de esta hoja en otra pestaña: ya se mandó; no se manda dos veces.
			await descartarBorrador(b).catch(() => null);
			acciones.inicioCalidad();
			return;
		}
		console.error('no se pudo guardar la muestra', error);
		acciones.inicioCalidad();
	} finally {
		comprometiendo.delete(b.capture_id);
	}
}

/**
 * §12.18: guardar la hoja corregida. Viaja como `muestra.corregir` sobre la versión vista, colgada de la mutación que la
 * trajo si sigue en la cola (fijada al elegir la hoja). El resumen lleva la hoja como queda y, si su humedad se sigue
 * ofreciendo, la tarjeta de la humedad: la humedad de hoy cuenta desde que entró, y una corrección que no la cambia no la
 * renueva (como `ENTRO_LA_HUMEDAD` del servidor).
 */
async function confirmarCorreccion(b: BorradorMuestra): Promise<void> {
	const base = b.corrige;
	if (base === undefined || comprometiendo.has(b.capture_id)) return;
	comprometiendo.add(b.capture_id);
	try {
		const r = regla();
		const cat = catalogoDefectos();
		if (r === null || !estadoCorreccion(b, r, cat).ok) {
			app.render();
			return;
		}
		const payload = armarPayloadCorreccion(b, r, cat);
		const letra = payload.letra_asignada as LetraCalidad;
		const persona = payload.persona_id as string;
		const ahoraIso = new Date(ahoraMs()).toISOString();
		const cambios = cambiosDeLaHoja(base, b, cat);
		const humedad = payload.humedad as number | null;
		const otraHumedad = humedad !== base.humedad;
		const letraPropuesta = proponer(b, r, cat)?.letra ?? null;
		const hoja = hojaDelPayload(payload, cat, {
			raiz_id: base.raiz_id,
			llenado_id: base.llenado_id,
			nro_muestra: base.nro_muestra,
			silo: base.silo,
			precinto: base.precinto,
			lote: base.lote,
			silo_estado: payload.contaminacion_grave === 1 && base.contaminacion_grave === 0 ? 'descartado' : base.silo_estado,
			letra_propuesta: letraPropuesta,
			letra_por: base.letra_por,
			cargada_por: base.cargada_por,
			hoja_desde: base.hoja_desde,
			cambiada_por: persona,
			cambiada_en: ahoraIso,
			desde_gestion: 0,
			humedad_desde: humedad === null ? null : otraHumedad ? ahoraIso : base.humedad_desde,
			humedad_por: humedad === null ? null : otraHumedad ? persona : base.humedad_por,
			con_bolsas: base.con_bolsas,
		});
		const sigueLaHumedad = otraHumedad || app.humedadesParaCorregir.some((m) => m.llenado_id === base.llenado_id && m.muestra_id === base.muestra_id);
		const tarjeta: TarjetaDeHumedad | undefined =
			humedad === null || payload.contaminacion_grave === 1 || !sigueLaHumedad || hoja.humedad_desde === null || hoja.humedad_por === null
				? undefined
				: {
						llenado_id: base.llenado_id,
						precinto: base.precinto,
						silo: base.silo,
						nro_muestra: base.nro_muestra,
						tomada_en: base.tomada_en,
						letra_asignada: letra,
						letra_propuesta: letraPropuesta,
						motivo_decision: payload.motivo_decision as string | null,
						densidad: payload.densidad as number,
						tamiz_bajo_40: payload.tamiz_bajo_40 as number,
						palos: payload.palos as number,
						contaminacion_grave: 0,
						defectos: JSON.stringify(hoja.defectos.map(({ grupo, grado }) => ({ grupo, grado }))),
						con_bolsas: base.con_bolsas,
						persona_id: hoja.humedad_por,
						cargada_en: hoja.humedad_desde,
					};
		const mutacion: MutacionAEncolar = {
			id: b.mutacion_id,
			tipo: 'muestra.corregir',
			payload,
			entidad: 'muestra',
			registro_id: base.muestra_id,
			personas: [persona],
			// Si la versión que se corrige sigue en la cola, la corrección cuelga de ella (§12.17 h. 6): se fijó al elegir la hoja.
			...(base.mutacion_id === undefined ? {} : { padre_id: base.mutacion_id }),
			resumen: {
				entidad: 'muestra',
				accion: 'hoja_corregida',
				fecha: fechaDeProduccion(ahoraMs()),
				silo: String(base.silo),
				precinto: base.precinto,
				letra,
				letra_antes: base.letra_asignada,
				cambios,
				hoja,
				...(tarjeta === undefined ? {} : { tarjeta }),
			},
		};
		await encolar(app.bd, [mutacion], [b.capture_id], ahoraIso);
		// Un hijo cuyo padre ya había caído nace en la bandeja con `padre_rechazado`: el comprobante lo dice ya.
		const caida = (await bandeja(app.bd)).find((f) => f.id === b.mutacion_id);
		if (caida) app.rechazos.set(b.mutacion_id, caida.motivo ?? '');
		recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
		app.dispararSync();
		irA({ tipo: 'calidad-hoja-corregida', borrador: b, letra, cambios, mutacion_id: b.mutacion_id });
	} catch (error) {
		if (error instanceof HojaIncompleta) {
			app.render();
			return;
		}
		if (error instanceof BorradorYaConfirmado) {
			await descartarBorrador(b).catch(() => null);
			acciones.inicioCalidad();
			return;
		}
		console.error('no se pudo guardar la corrección', error);
		acciones.inicioCalidad();
	} finally {
		comprometiendo.delete(b.capture_id);
	}
}

/** §12.18: el comprobante de la hoja corregida, o lo que dijo la oficina si la rechazó con la pantalla a la vista. */
export function pintarHojaCorregida(cuerpo: HTMLElement, pieNodo: HTMLElement, vista: Extract<Vista, { tipo: 'calidad-hoja-corregida' }>): void {
	const base = vista.borrador.corrige;
	const referencia = base === undefined ? '' : `Silo ${base.silo} · precinto ${base.precinto} · ${muestraNro(base.nro_muestra)}`;
	const rechazo = app.rechazos.get(vista.mutacion_id);
	if (rechazo !== undefined) {
		pregunta(cuerpo, 'La hoja no se corrigió', referencia);
		cuerpo.appendChild(flag('stop', 'La oficina no lo aceptó.', `${queMotivo(rechazo)}. La hoja sigue como estaba.`));
	} else {
		cuerpo.appendChild(el('div', { class: 'done' }, 'Hoja corregida'));
		const antes = base?.letra_asignada ?? vista.letra;
		const filas: Array<[string, string]> = [
			[base === undefined ? 'Silo' : `Silo ${base.silo}`, base === undefined ? '' : `precinto ${base.precinto}`],
			['Muestra', base?.nro_muestra === null || base === undefined ? 'nueva' : `nº ${base.nro_muestra}`],
			['Cambió', vista.cambios.length > 0 ? vista.cambios.join('; ') : 'la letra'],
			['Letra', `${textoLetra(vista.letra)}${vista.letra !== antes ? ` · era ${textoLetra(antes)}` : ' · sin cambios'}`],
		];
		const ticket = el('div', { class: 'ticket' });
		for (const [etiqueta, valor] of filas) {
			const fila = el('div', { class: 'row' });
			fila.appendChild(el('span', undefined, etiqueta));
			fila.appendChild(el('b', undefined, valor));
			ticket.appendChild(fila);
		}
		cuerpo.appendChild(ticket);
		if (vista.letra !== antes && base?.con_bolsas === 1) cuerpo.appendChild(flag('warn', 'El precinto ya tiene bolsas:', 'la oficina revisa sus pallets.'));
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, app.enLinea ? 'Subiendo.' : 'Sin señal: sube sola cuando haya.'));
	}
	const volver = el('button', { class: 'btn ancho', type: 'button' }, 'Volver a la lista');
	volver.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(volver);
}

/** El comprobante muestra la letra que VIAJÓ: no se vuelve a proponer con un catálogo que pudo cambiar en el medio. */
export function pintarListoCalidad(cuerpo: HTMLElement, pieNodo: HTMLElement, b: BorradorMuestra, letra: LetraCalidad): void {
	cuerpo.appendChild(el('div', { class: 'done' }, 'Muestra guardada'));
	const ticket = el('div', { class: 'ticket' });
	const filas: Array<[string, string]> = [
		[`Silo ${b.silo_numero}`, `precinto ${b.precinto}`],
		['Letra', textoLetra(letra)],
		['Densidad · humedad', `${textoDe(b, 'densidad')} · ${sinHumedad(b) ? 'falta (letra provisoria)' : `${textoDe(b, 'humedad')} %`}`],
	];
	if (b.contaminacion_grave === 1) filas.push(['El silo', 'queda descartado cuando suba']);
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		ticket.appendChild(fila);
	}
	cuerpo.appendChild(ticket);
	cuerpo.appendChild(
		el('div', { class: 'qsub despues' }, `${app.enLinea ? 'Subiendo.' : 'Sin señal: sube solo cuando haya.'} El número de muestra lo pone la oficina; se ve en «lo cargado».`),
	);
	const otra = el('button', { class: 'btn go ancho', type: 'button' }, 'Otra muestra');
	otra.onclick = () => acciones.inicioCalidad();
	pieNodo.appendChild(otra);
}

// ── Rehacer una muestra que el servidor rechazó por regla desactualizada ──

/**
 * La hoja vuelve desde lo que viajó (§5.2.12): las mediciones intactas, la letra y
 * el motivo a decidir de nuevo con la regla que bajó, y con identidad nueva. El
 * incidente se acusa: lo que lo reemplaza es esta hoja.
 */
const rehaciendo = new Set<string>();

export async function rehacerMuestraRechazada(incidente: FilaOutbox): Promise<void> {
	if (rehaciendo.has(incidente.id)) return;
	rehaciendo.add(incidente.id);
	try {
		await rehacer(incidente);
	} finally {
		rehaciendo.delete(incidente.id);
	}
}

async function rehacer(incidente: FilaOutbox): Promise<void> {
	const payload = (incidente.payload ?? {}) as Record<string, unknown>;
	const llenadoId = typeof payload.llenado_id === 'string' ? payload.llenado_id : incidente.registro_id;
	// Si mientras tanto se empezó OTRA hoja del mismo silo, no se pisa: la analista
	// decide cuál vale (Codex, ronda del diff, 11/9).
	const previa = await leerHoja(llenadoId);
	if (previa) {
		const hojas = await listarHojas();
		irA({ tipo: 'calidad-lista', hojas, aviso: `El silo ${previa.silo_numero} ya tiene una hoja empezada. Descartala desde la hoja si querés recuperar la rechazada.` });
		return;
	}
	const enLista = app.paraMuestra.find((s) => s.llenado_id === llenadoId);
	const resumen = incidente.resumen.entidad === 'muestra' ? incidente.resumen : null;
	const foto = enLista
		? {
				campania_id: enLista.campania_id,
				silo_numero: numeroDeSilo(enLista),
				precinto: enLista.precinto,
				lote: (app.catalogos?.lotes_campo ?? []).find((l) => l.id === enLista.lote_campo_id)?.nombre ?? '—',
				neto_g: enLista.neto_g,
				cerrado_en: enLista.cerrado_en,
			}
		: { campania_id: '', silo_numero: resumen?.silo ?? '—', precinto: resumen?.precinto ?? '—', lote: '—', neto_g: 0, cerrado_en: '' };
	const hoja = hojaDesdePayloadRechazado(payload, { capture_id: llenadoId, mutacion_id: generarUlid(), muestra_id: generarUlid() }, foto, ahoraMs());
	// Primero la hoja, después el acuse: si el guardado falla, el incidente sigue en la
	// bandeja y no se perdió nada.
	await guardarBorrador(hoja);
	await acusarIncidente(app.bd, incidente.id);
	irA({ tipo: 'calidad-hoja', borrador: hoja });
}
